// S8 — recording compliance dashboard.
//
// One row per camera with the day's expected / actual / gap-count /
// compliance score. CSV export is client-side from the in-memory rows
// — no extra API call. Recalculate queues a Celery task and the user
// re-clicks the date picker after a moment to see the new numbers.
import { useState } from "react";
import { DownloadIcon, RotateCwIcon } from "lucide-react";

import {
  useCompliance,
  useRecalculateCompliance,
  type ComplianceItem,
} from "@/api/playback";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";


function scoreColor(pct: number): string {
  if (pct >= 95) return "bg-emerald-500/15 text-emerald-700";
  if (pct >= 80) return "bg-amber-500/15 text-amber-700";
  return "bg-red-500/15 text-red-700";
}


function downloadCsv(rows: ComplianceItem[], date: string) {
  const headers = [
    "Camera", "Entity", "Expected (s)", "Actual (s)", "Gaps",
    "Compliance (%)", "Date",
  ];
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(
      [
        JSON.stringify(r.camera_name ?? r.camera_id),
        JSON.stringify(r.entity_name ?? ""),
        r.expected_seconds,
        r.actual_seconds,
        r.gap_count,
        r.compliance_pct.toFixed(2),
        r.date,
      ].join(","),
    );
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `compliance_${date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}


function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}


export function ComplianceDashboard() {
  const [date, setDate] = useState<string>(todayIso());
  const compliance = useCompliance(date);
  const recalc = useRecalculateCompliance();
  const [message, setMessage] = useState<string | null>(null);

  async function handleRecalculate() {
    setMessage(null);
    try {
      const res = await recalc.mutateAsync({ date });
      setMessage(`Queued (task ${res.task_id.slice(0, 8)}…). Refresh in a moment.`);
    } catch {
      setMessage("Failed to queue recalculation.");
    }
  }

  const items = compliance.data?.items ?? [];

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Compliance · {date}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadCsv(items, date)}
              disabled={items.length === 0}
            >
              <DownloadIcon className="mr-1 h-3.5 w-3.5" /> Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleRecalculate()}
              disabled={recalc.isPending}
            >
              <RotateCwIcon className="mr-1 h-3.5 w-3.5" />
              {recalc.isPending ? "Queuing…" : "Recalculate"}
            </Button>
            {message && (
              <span className="text-xs text-muted-foreground">{message}</span>
            )}
          </div>

          {compliance.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No snapshots for this date. Click <strong>Recalculate</strong> to
              backfill.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Camera</th>
                  <th className="px-3 py-2 text-left">Entity</th>
                  <th className="px-3 py-2 text-right">Expected</th>
                  <th className="px-3 py-2 text-right">Actual</th>
                  <th className="px-3 py-2 text-right">Gaps</th>
                  <th className="px-3 py-2 text-right">Score</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.camera_id} className="border-b last:border-0">
                    <td className="px-3 py-2 text-xs">{r.camera_name}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {r.entity_name ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {Math.round(r.expected_seconds / 60)}m
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {Math.round(r.actual_seconds / 60)}m
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {r.gap_count}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span
                        className={cn(
                          "rounded px-2 py-0.5 text-[11px] font-medium",
                          scoreColor(r.compliance_pct),
                        )}
                      >
                        {r.compliance_pct.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
